import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import logo from '../../logo.png';
import { Users, ChevronDown, ChevronRight, Download, Filter } from 'lucide-react';

// Colores y estilos por nivel jerárquico
const CONFIG_ROL = {
  coordinador_general:     { color: '#7c3aed', bg: '#f5f3ff', border: '#7c3aed', label: 'Coord. General',     icono: '👑', nivel: 1 },
  coordinador_territorial: { color: '#1d4ed8', bg: '#eff6ff', border: '#1d4ed8', label: 'Coord. Territorial', icono: '🗺', nivel: 2 },
  coordinador_sectorial:   { color: '#0369a1', bg: '#f0f9ff', border: '#0369a1', label: 'Coord. Sectorial',   icono: '🏛', nivel: 3 },
  seccional:               { color: '#059669', bg: '#f0fdf4', border: '#059669', label: 'Seccional',          icono: '📋', nivel: 4 },
  operador:                { color: '#d97706', bg: '#fffbeb', border: '#d97706', label: 'Operador',            icono: '⚙️', nivel: 5 },
  promotor:                { color: '#dc2626', bg: '#fef2f2', border: '#dc2626', label: 'Promotor',            icono: '🎯', nivel: 6 },
};

// ── Tarjeta de nodo ──────────────────────────────────────────────────────────
function NodoPersona({ miembro, hijos, nivel = 0, expandidoGlobal }) {
  const [expandido, setExpandido] = useState(nivel < 2);
  const config = CONFIG_ROL[miembro.rol] || { color: '#64748b', bg: '#f8fafc', border: '#64748b', label: miembro.rol, icono: '👤', nivel: 9 };
  const tieneHijos = hijos && hijos.length > 0;

  useEffect(() => {
    setExpandido(expandidoGlobal);
  }, [expandidoGlobal]);

  return (
    <div className="flex flex-col items-center">
      {/* Línea superior de conexión */}
      {nivel > 0 && (
        <div className="w-px h-6" style={{ background: '#cbd5e1' }} />
      )}

      {/* Tarjeta del nodo */}
      <div className="relative">
        <div
          onClick={() => tieneHijos && setExpandido(!expandido)}
          className={`relative rounded-2xl shadow-md border-2 transition-all duration-200 ${tieneHijos ? 'cursor-pointer hover:shadow-lg hover:scale-105' : ''}`}
          style={{
            background: config.bg,
            borderColor: config.border,
            minWidth: '160px',
            maxWidth: '200px',
          }}>
          {/* Header de color */}
          <div className="rounded-t-xl px-3 py-2 flex items-center justify-between"
            style={{ background: config.color }}>
            <span className="text-white text-xs font-semibold">{config.label}</span>
            <span className="text-lg">{config.icono}</span>
          </div>

          {/* Contenido */}
          <div className="px-3 py-2">
            <p className="font-bold text-gray-800 text-sm leading-tight">
              {miembro.nombre} {miembro.apellido_paterno}
            </p>
            {miembro.numero_seccion && (
              <p className="text-xs text-gray-500 mt-0.5">Secc. {miembro.numero_seccion}</p>
            )}
            {miembro.nombre_estructura && (
              <p className="text-xs text-gray-400 truncate">{miembro.nombre_estructura}</p>
            )}
            {miembro.nombre_grupo && (
              <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-xs font-medium"
                style={{ background: config.bg, color: config.color, border: `1px solid ${config.border}` }}>
                {miembro.nombre_grupo}
              </span>
            )}
          </div>

          {/* Indicador de hijos */}
          {tieneHijos && (
            <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full flex items-center justify-center shadow-sm border-2"
              style={{ background: config.color, borderColor: 'white' }}>
              {expandido
                ? <ChevronDown size={12} className="text-white" />
                : <ChevronRight size={12} className="text-white" />}
            </div>
          )}

          {/* Badge de subordinados */}
          {tieneHijos && (
            <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white shadow"
              style={{ background: config.color }}>
              {hijos.length}
            </div>
          )}
        </div>
      </div>

      {/* Hijos */}
      {tieneHijos && expandido && (
        <div className="flex flex-col items-center mt-3">
          {/* Línea inferior */}
          <div className="w-px h-4" style={{ background: '#cbd5e1' }} />

          {/* Rama horizontal */}
          {hijos.length > 1 && (
            <div className="relative flex items-start justify-center"
              style={{ width: `${hijos.length * 220}px` }}>
              <div className="absolute top-0 h-px"
                style={{
                  background: '#cbd5e1',
                  left: '110px',
                  right: '110px',
                }} />
            </div>
          )}

          {/* Nodos hijos */}
          <div className="flex gap-6 items-start">
            {hijos.map((hijo, idx) => (
              <NodoPersona
                key={hijo.miembro.id}
                miembro={hijo.miembro}
                hijos={hijo.hijos}
                nivel={nivel + 1}
                expandidoGlobal={expandidoGlobal}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function OrganigramaEstructura() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [estructuras, setEstructuras] = useState([]);
  const [estructuraSeleccionada, setEstructuraSeleccionada] = useState(null);
  const [arbol, setArbol] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingArbol, setLoadingArbol] = useState(false);
  const [expandidoGlobal, setExpandidoGlobal] = useState(true);
  const [stats, setStats] = useState({});

  useEffect(() => {
    fetchEstructuras();
  }, []);

  const fetchEstructuras = async () => {
    try {
      setLoading(true);
      const res = await api.get('/estructuras');
      setEstructuras(res.data.estructuras || []);
      // Seleccionar la primera por defecto
      if (res.data.estructuras?.length > 0) {
        cargarArbol(res.data.estructuras[0]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const cargarArbol = async (estructura) => {
    setEstructuraSeleccionada(estructura);
    setLoadingArbol(true);
    try {
      const res = await api.get(`/asignaciones/arbol/${estructura.id}`);
      setArbol(res.data.arbol || []);
      setStats(res.data.stats || {});
    } catch (error) {
      console.error(error);
      setArbol([]);
    } finally {
      setLoadingArbol(false);
    }
  };

  // Calcular stats locales si no vienen del backend
  const contarNodos = (nodos) => {
    let total = 0;
    nodos.forEach(n => {
      total += 1 + contarNodos(n.hijos || []);
    });
    return total;
  };

  const totalMiembros = contarNodos(arbol);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="text-white px-6 py-4 flex items-center justify-between shadow-lg sticky top-0 z-40"
        style={{ background: 'linear-gradient(135deg, #14532d 0%, #5c1022 100%)' }}>
        <div className="flex items-center gap-3">
          <img src={logo} alt="DOMINIUM" className="h-16" />
          <div>
            <h1 className="font-bold text-lg leading-tight">Organigrama</h1>
            <p className="text-green-200 text-xs">Vista jerárquica de la estructura</p>
          </div>
        </div>
        <button onClick={() => navigate('/dashboard')}
          className="bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg text-sm transition">
          ← Dashboard
        </button>
      </header>

      <main className="p-4 md:p-6">

        {/* Selector de estructura + controles */}
        <div className="flex flex-wrap gap-3 mb-6 items-center">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-500" />
            <select
              value={estructuraSeleccionada?.id || ''}
              onChange={(e) => {
                const est = estructuras.find(x => x.id === e.target.value);
                if (est) cargarArbol(est);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 bg-white">
              {estructuras.map(e => (
                <option key={e.id} value={e.id}>{e.nombre_estructura}</option>
              ))}
            </select>
          </div>

          <button
            onClick={() => setExpandidoGlobal(true)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition">
            Expandir todo
          </button>
          <button
            onClick={() => setExpandidoGlobal(false)}
            className="px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition">
            Colapsar todo
          </button>

          {/* Stats */}
          <div className="ml-auto flex gap-3">
            {Object.entries(CONFIG_ROL).map(([rol, cfg]) => {
              const count = stats[rol] || 0;
              if (!count) return null;
              return (
                <div key={rol} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium"
                  style={{ background: cfg.bg, borderColor: cfg.border, color: cfg.color }}>
                  <span>{cfg.icono}</span>
                  <span>{cfg.label}: {count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Área del organigrama */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-auto"
          style={{ minHeight: '500px' }}>

          {loading || loadingArbol ? (
            <div className="flex flex-col items-center justify-center h-64">
              <div className="w-10 h-10 border-4 border-green-500 border-t-transparent rounded-full animate-spin mb-3" />
              <p className="text-gray-500 text-sm">Cargando organigrama...</p>
            </div>
          ) : arbol.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64">
              <Users className="text-gray-300 mb-3" size={48} />
              <p className="text-gray-500 text-sm">No hay miembros en esta estructura</p>
              <p className="text-gray-400 text-xs mt-1">Da de alta miembros desde Gestión de Usuarios</p>
            </div>
          ) : (
            <div className="p-8" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              {/* Nombre de la estructura */}
              <div className="text-center mb-8">
                <div className="inline-flex items-center gap-3 px-6 py-3 rounded-2xl shadow-sm border-2"
                  style={{ background: '#f8fafc', borderColor: '#e2e8f0' }}>
                  <span className="text-2xl">🏛</span>
                  <div className="text-left">
                    <p className="font-bold text-gray-800 text-lg">{estructuraSeleccionada?.nombre_estructura}</p>
                    <p className="text-gray-500 text-xs capitalize">
                      {estructuraSeleccionada?.tipo_estructura} · {totalMiembros} miembro{totalMiembros !== 1 ? 's' : ''}
                    </p>
                  </div>
                </div>

                {/* Línea al primer nivel */}
                <div className="flex justify-center mt-2">
                  <div className="w-px h-6" style={{ background: '#cbd5e1' }} />
                </div>
              </div>

              {/* Árbol */}
              <div style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <div style={{ minWidth: "max-content", margin: "0 auto" }} className="flex justify-center">
                {arbol.length === 1 ? (
                  <NodoPersona
                    miembro={arbol[0].miembro}
                    hijos={arbol[0].hijos}
                    nivel={0}
                    expandidoGlobal={expandidoGlobal}
                  />
                ) : (
                  <div className="flex gap-8 items-start">
                    {arbol.map((nodo, i) => (
                      <NodoPersona
                        key={nodo.miembro.id}
                        miembro={nodo.miembro}
                        hijos={nodo.hijos}
                        nivel={0}
                        expandidoGlobal={expandidoGlobal}
                      />
                    ))}
                  </div>
                )}
              </div>
              </div>
            </div>
          )}
        </div>

        {/* Leyenda */}
        <div className="mt-4 flex flex-wrap gap-3 justify-center">
          {Object.entries(CONFIG_ROL).map(([rol, cfg]) => (
            <div key={rol} className="flex items-center gap-1.5 text-xs text-gray-600">
              <div className="w-3 h-3 rounded-full" style={{ background: cfg.color }} />
              <span>{cfg.label}</span>
            </div>
          ))}
        </div>

        <p className="text-center text-gray-400 text-xs mt-6">
          DOMINIUM © 2026 — Zacatlán, Puebla | GARCOR Design
        </p>
      </main>
    </div>
  );
}
